import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import { Button, Spin, Typography } from 'antd'
import { produce } from 'immer'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { SectionType, useGetPostQuery, useGetTimelinePostsQuery } from '@/generated/graphql'
import { PostCard } from '@/modules/post/post-card'
import { ga, traceEvent } from '@/utils/ga'

import { OnboardingPageProps } from '../onboarding.interface'
import { OnboardingPageLayout } from './onboarding-page-layout.component'

export const OnboardingPage1: React.FC<OnboardingPageProps> = ({ onNext, onPrevious }) => {
  const { t } = useTranslation()

  const [pageStartTime, setPageStartTime] = React.useState(moment())

  // 页面停留时间
  React.useEffect(() => {
    setPageStartTime(moment())
    return () => {
      const category = traceEvent.onboarding
      ga('event', category.events.expose_time_page, {
        event_category: category.event_category,
        event_label: '1',
        value: moment().diff(pageStartTime, 'ms'),
      })
    }
  }, [])

  const { data: innovationData } = useGetPostQuery({
    fetchPolicy: 'network-only',
    variables: {
      postId: 3287,
      parentCommentPagination: {
        last: 0,
      },
      childCommentPagination: {
        last: 0,
      },
    },
  })

  const { data } = useGetTimelinePostsQuery({
    variables: {
      groupId: 24,
      userId: 0,
      sectionId: SectionType.Null,
      departmentId: 0,
      withViewerFollowing: false,
      withUserCreated: false,
      withGroup: true,
      withSection: false,
      withDepartmentJournal: false,
      withAllPosts: false,
      pagination: {
        last: 1,
      },
      parentCommentPagination: {
        last: 1,
      },
      childCommentPagination: {
        last: 1,
      },
      filter: {},
    },
  })

  const innovationPost = innovationData?.post
  let communicationPost = data?.group?.posts.edges.map(({ node }) => node)[0]

  if (communicationPost) {
    communicationPost = produce(communicationPost, (draft) => {
      draft.content = draft.content.split('\n')[0]
    })
  }

  const postDiv = (header: React.ReactNode, post: React.ReactNode, style: any) => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', ...style }}>
        <div style={{ marginBottom: 8, minHeight: 120 }}>{header}</div>
        {post}
      </div>
    )
  }

  const left = postDiv(
    <>
      <ul style={{ paddingInlineStart: 20 }}>
        <li style={{ color: '#48bbb5' }}>
          <Typography.Title style={{ marginTop: 0, color: '#48bbb5' }} level={5}>
            {t('onboarding.page1.content')}
          </Typography.Title>
        </li>
      </ul>
      <div style={{ marginTop: 8, paddingLeft: 40 }}>
        <Typography.Title style={{ marginTop: 0, color: '#48bbb5' }} level={5}>
          {t('onboarding.page1.ending')}
        </Typography.Title>
      </div>
    </>,
    innovationPost ? (
      <PostCard
        post={innovationPost!}
        hideHeaderMenu={true}
        isCommentControllable={false}
        disableContentPics={true}
        disableAction={true}
        disableCreateComment={true}
      />
    ) : (
      <Spin delay={300} size='large' />
    ),
    { marginRight: 12 }
  )

  const right = postDiv(
    <ul style={{ paddingInlineStart: 20 }}>
      <li style={{ color: '#48bbb5' }}>
        <Typography.Title style={{ marginTop: 0, color: '#48bbb5' }} level={5}>
          {t('onboarding.page1.title')}
        </Typography.Title>
      </li>
    </ul>,
    communicationPost ? (
      <div style={{ overflow: 'scroll', maxHeight: 350 }}>
        <PostCard
          post={communicationPost!}
          hideHeaderMenu={true}
          isCommentControllable={false}
          disableAction={true}
          disableContentBackground={true}
          disableCreateComment={true}
        />
      </div>
    ) : (
      <Spin delay={300} size='large' />
    ),
    { marginLeft: 12 }
  )

  const footer = (
    <>
      <Button
        type='primary'
        onClick={() => {
          const category = traceEvent.onboarding
          ga('event', category.events.click_prev_page, {
            event_category: category.event_category,
            event_label: '1',
          })
          onPrevious!()
        }}
      >
        <LeftOutlined />
        {t('onboarding.previous')}
      </Button>
      <Button
        type='primary'
        onClick={() => {
          const category = traceEvent.onboarding
          ga('event', category.events.click_next_page, {
            event_category: category.event_category,
            event_label: '1',
          })
          onNext!()
        }}
      >
        {t('onboarding.next')}
        <RightOutlined />
      </Button>
    </>
  )

  return <OnboardingPageLayout header='ABOUT TALE' left={left} right={right} footer={footer} percent={1} />
}
